import { apply, burst, celebrate, onceForever, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say } from '../ui/dialog';
import type { ObjectId } from '../world/mapdef';
import { MARKET_OPEN } from '../world/npcsHlcity';
import { registerObjects, registerTalk } from './registry';
import { stamp } from './passport';
import { hualienShuttle, order, quiz } from './trips';

/** Interactions of Hualien city, Dongdamen Night Market, Pine Garden and Ji'an (hlcity package). */
const t = () => m().hlcity;
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];
const marketOpen = () => S().time >= MARKET_OPEN;

/** A stall of the night market: covered by day, ordering in English at night. */
function nightStall(id: ObjectId, icon: string, food: keyof ReturnType<typeof t>['food'], fx: { energy?: number; mood?: number }): () => void {
  return () => {
    if (!marketOpen()) return say({ who: objName(id), icon: '⛺', text: t().closed });
    order(id, icon, t().food[food], fx, () => {
      if (stamp('dongdamen')) toast(t().stampHint);
    });
  };
}

/** A fact, its English sentence read aloud, and one action that pays out once a day. */
function fact(id: ObjectId, icon: string, text: string, en: string, action: string, done: string, fx: Parameters<typeof apply>[0], after?: () => void): void {
  sayEnglish(en, 0.85);
  say({
    who: objName(id),
    icon,
    text: `${text}\n\n🗣️ “${en}”`,
    choices: [
      {
        text: action,
        run: () => {
          sfx('ok');
          burst('sparkle', 5);
          if (onceToday(`hc.${id}`)) apply(fx);
          after?.();
          say({ who: objName(id), icon, text: done });
        },
      },
      { text: t().back },
    ],
  });
}

/** A fact followed by a one-question quiz in English (first answer right; stars once ever). */
function factQuiz(id: ObjectId, icon: string, text: string, q: string, a: readonly string[], right: string, wrong: string, after?: () => void): void {
  quiz(objName(id), icon, text, q, a, right, wrong, () => {
    if (onceForever(`hc.quiz.${id}`)) apply({ stars: 3, study: 2 });
    else if (onceToday(`hc.quiz.${id}`)) apply({ mood: 4 });
    after?.();
  });
}

function balloonDarts(): void {
  const tx = t();
  const who = objName('hc_balloon');
  if (!marketOpen()) return say({ who, icon: '⛺', text: tx.closed });
  say({
    who,
    icon: '🎈',
    text: tx.balloon,
    choices: [
      {
        text: tx.play,
        run: () => {
          if (S().coins < 10) return toast(m().world3.market.poor);
          apply({ coins: -10, minutes: 10 }, { float: false });
          sfx('pop');
          const n = 1 + Math.floor(Math.random() * 5);
          const board = '💥'.repeat(n) + '🎈'.repeat(5 - n);
          const others = [n - 1, n + 1, n === 5 ? 3 : 5].filter((k) => k >= 0 && k <= 5 && k !== n).slice(0, 2);
          quiz(who, '🎯', board, tx.balloonQ, [n, ...others].map((k) => tx.popped(tx.nums[k])), tx.balloonRight, tx.balloonWrong, () => {
            if (onceToday('hc.balloon')) apply({ mood: 12, stars: 2, study: 1 });
            if (n >= 3) celebrate();
            if (stamp('dongdamen')) toast(tx.stampHint);
          });
        },
      },
      { text: tx.look },
    ],
  });
}

function photoSpot(): void {
  const tx = t();
  const who = objName('hc_photo');
  quiz(who, '📸', tx.photo, tx.photoHow, tx.photoA, tx.photoRight, tx.photoWrong, () => {
    sfx('ring');
    celebrate();
    if (onceToday('hc.photo')) apply({ mood: 12, stars: 2 });
    stamp('jian');
    S().forever['hc.photo'] = true;
  });
}

function singer(): void {
  const tx = t();
  const who = npcName('hc_singer');
  sayEnglish(tx.singerEn, 0.9);
  say({
    who,
    icon: '🎤',
    text: `${pick(tx.talk.hc_singer.lines)}\n\n${tx.singer}\n\n🗣️ “${tx.singerEn}”`,
    choices: [
      {
        text: tx.clap,
        run: () => {
          sfx('fanfare');
          burst('note', 8);
          if (onceToday('hc.singer')) apply({ mood: 15, stars: 2, minutes: 15 });
          if (stamp('dongdamen')) toast(tx.stampHint);
          say({ who, icon: '🎶', text: tx.clapped });
        },
      },
      { text: tx.back },
    ],
  });
}

function taxi(): void {
  const tx = t();
  quiz(npcName('hc_taxiDriver'), '🚕', pick(tx.talk.hc_taxiDriver.lines), tx.taxiQ, tx.taxiA, tx.taxiRight, tx.taxiWrong, () => {
    if (onceToday('hc.taxi')) apply({ stars: 1, study: 1 });
  });
}

const OBJ: Partial<Record<string, () => void>> = {
  hc_shuttle: () => hualienShuttle('city'),
  hc_mural: () => factQuiz('hc_mural', '🎨', t().mural, t().muralQ, t().muralA, t().muralRight, t().muralWrong),
  hc_shrine: () => fact('hc_shrine', '🙏', t().shrine, t().shrineEn, t().pray, t().prayed, { mood: 6 }),
  hc_marketMap: () => factQuiz('hc_marketMap', '🗺️', t().marketMap, t().marketQ, t().marketA, t().marketRight, t().marketWrong),
  hc_dumpling: nightStall('hc_dumpling', '🥟', 'dumpling', { energy: 28, mood: 6 }),
  hc_beefNoodle: nightStall('hc_beefNoodle', '🍜', 'beefNoodle', { energy: 35, mood: 6 }),
  hc_xlb: nightStall('hc_xlb', '🥢', 'xlb', { energy: 25, mood: 8 }),
  hc_sausage: nightStall('hc_sausage', '🌭', 'sausage', { energy: 18, mood: 8 }),
  hc_bamboo: nightStall('hc_bamboo', '🎋', 'bamboo', { energy: 30, mood: 8 }),
  hc_crafts: nightStall('hc_crafts', '🧶', 'crafts', { mood: 15 }),
  hc_icecream: nightStall('hc_icecream', '🍦', 'icecream', { energy: 10, mood: 14 }),
  hc_tea: nightStall('hc_tea', '🧋', 'tea', { energy: 8, mood: 12 }),
  hc_balloon: balloonDarts,
  hc_creative: () => fact('hc_creative', '🏭', t().creative, t().creativeEn, t().creativeLook, t().creativeDone, { mood: 5, study: 1 }),
  hc_pineGarden: () => factQuiz('hc_pineGarden', '🌲', t().pine, t().pineQ, t().pineA, t().pineRight, t().pineWrong),
  hc_riverMouth: () => fact('hc_riverMouth', '🔭', t().riverMouth, t().riverEn, t().lookAround, t().lookedRiver, { mood: 10, stars: 1 }),
  hc_bikePath: () => factQuiz('hc_bikePath', '🚲', t().bikePath, t().bikeQ, t().bikeA, t().bikeRight, t().bikeWrong),
  hc_seafront: () => fact('hc_seafront', '🌊', t().seafront, t().seaEn, t().listen, t().listened, { mood: 12, energy: 4 }, () => sfx('water')),
  hc_container: () => order('hc_container', '☕', t().food.container, { energy: 10, mood: 12 }, () => stamp('jian')),
  hc_photo: photoSpot,
  hc_temple: () => fact('hc_temple', '🛕', t().temple, t().templeEn, t().wish, t().wished, { mood: 10, stars: 2 }, () => sfx('bell')),
  hc_buddha88: () => factQuiz('hc_buddha88', '🗿', t().buddha, t().buddhaQ, t().buddhaA, t().buddhaRight, t().buddhaWrong),
  hc_paddy: () => factQuiz('hc_paddy', '🌾', t().paddy, t().paddyQ, t().paddyA, t().paddyRight, t().paddyWrong),
  hc_store: () => order('hc_store', '🏪', t().food.store, { energy: 12, mood: 4 }),
};
registerObjects(OBJ);

const STALL_OF: Record<string, ObjectId> = {
  hc_dumplingGrandpa: 'hc_dumpling',
  hc_sausageVendor: 'hc_sausage',
  hc_balloonVendor: 'hc_balloon',
};

registerTalk((id) => {
  if (!id.startsWith('hc_')) return false;
  const tx = t();
  if (id === 'hc_singer') return singer(), true;
  if (id === 'hc_taxiDriver') return taxi(), true;
  const talk = tx.talk[id as keyof typeof tx.talk];
  if (!talk) return false;
  const who = npcName(id);
  const line = pick(talk.lines);
  sayEnglish(talk.en, 0.9);
  const text = `${line}\n\n🗣️ “${talk.en}”`;
  const stall = STALL_OF[id] ?? (id === 'hc_amisGrandma' && marketOpen() ? 'hc_crafts' : undefined);
  const action =
    stall && marketOpen()
      ? { text: `${objName(stall)} →`, run: () => OBJ[stall]?.() }
      : id === 'hc_shuttleGuide'
        ? { text: tx.shuttleRide, run: () => hualienShuttle('city') }
        : id === 'hc_barista'
          ? { text: `${objName('hc_container')} →`, run: () => OBJ.hc_container?.() }
          : null;
  if (onceToday(`hc.talk.${id}`)) apply({ mood: 3 }, { float: false });
  say({ who, icon: '💬', text, choices: action ? [action, { text: tx.back }] : undefined });
  return true;
});

